import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLanguage } from "@/i18n";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Services from "@/components/Services";
import Stack from "@/components/Stack";
import Why from "@/components/Why";
import Clients from "@/components/Clients";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import PrivacyPage from "@/components/PrivacyPage";
import NetworkCanvas from "@/components/NetworkCanvas";

class ErrorBoundary extends Component {
  state = { err: false };
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    if (this.state.err) return <div className="min-h-screen bg-slate-950" />;
    return this.props.children;
  }
}

function LenisScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// After the native FormSubmit POST redirects back to /#sent, greet the user.
// Hash is used instead of a query string: the preview host strips query params on load.
// Read at render time (not in an effect) so the banner is deterministic.
function SentNotice() {
  const { t } = useLanguage();
  const sent = typeof window !== "undefined" && window.location.hash === "#sent";
  useEffect(() => {
    if (sent) window.history.replaceState({}, "", window.location.pathname);
  }, [sent]);
  if (!sent) return null;
  return (
    <div
      data-testid="sent-banner"
      className="fixed left-1/2 top-20 z-[70] flex -translate-x-1/2 items-center gap-2 rounded-full border border-amber-400/40 bg-slate-950/90 px-5 py-2.5 text-sm font-semibold text-amber-200 shadow-[0_8px_40px_rgba(251,146,60,0.25)] backdrop-blur-md"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-70" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" />
      </span>
      {t.contact.toast}
    </div>
  );
}

function Site() {
  return (
    <div className="relative min-h-screen bg-slate-950 text-slate-100 antialiased selection:bg-orange-400/30 selection:text-orange-100">
      <div className="noise" aria-hidden="true" />
      {/* One global animated network behind every section */}
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        <NetworkCanvas />
      </div>
      <LenisScroll />
      <Navbar />
      <main className="relative z-10">
        <Hero />
        <Marquee />
        <Services />
        <Stack />
        <Why />
        <Clients />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <ErrorBoundary>
          <ScrollToTop />
          <SentNotice />
          <Routes>
            <Route path="/" element={<Site />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="*" element={<Site />} />
          </Routes>
          <CookieBanner />
          <Toaster position="bottom-right" />
        </ErrorBoundary>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
